import { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Plus, X, MessageSquare, RefreshCw, ChevronRight, DollarSign, TrendingUp, CheckSquare, CalendarDays, Users, FileText } from 'lucide-react';

// Roadmap page — per-tenant pain points / opportunities / initiatives / risks.
// Powered by /api/roadmap (modules/roadmap/routes.js).
//
// Canonical in tenant-app-template; CTS and Rock River replicate this file
// byte-identically. Edits land here first, then mirror.
//
// Phase 3a scope: items list with filters/sort, detail drawer (item +
// milestones + comments), status workflow chips. ROI Dashboard,
// Commitments view, and Meetings/transcripts are Phase 3b.

const API_BASE = '/api/roadmap';

function getToken() { return localStorage.getItem('mickey_token') || ''; }
function authHeaders(extra = {}) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...extra };
}

// Parse a JSON error body safely. Server may sometimes return HTML or text
// on certain failures (proxy 502, etc.) — falling back to status text keeps
// the surfaced message useful instead of "Unexpected token <" parser noise.
async function readErr(r) {
  try { return (await r.json())?.error || r.statusText || `HTTP ${r.status}`; }
  catch { return r.statusText || `HTTP ${r.status}`; }
}

// Encode a path segment defensively. itemIds are server-issued UUIDs so this
// is precautionary, but means any malformed id won't break the URL silently.
function encId(id) { return encodeURIComponent(String(id || '')); }

// Status → color mapping. Same vocabulary as the schema CHECK constraint.
const STATUS_STYLES = {
  backlog:      { bg: '#e5e7eb',                color: '#374151', label: 'Backlog' },
  selected:     { bg: 'rgba(59,130,246,0.15)',  color: '#1d4ed8', label: 'Selected' },
  in_progress:  { bg: 'rgba(245,158,11,0.18)',  color: '#92400e', label: 'In Progress' },
  done:         { bg: 'rgba(22,163,74,0.15)',   color: '#15803d', label: 'Done' },
  wont_do:      { bg: 'rgba(100,116,139,0.18)', color: '#475569', label: "Won't Do" },
  blocked:      { bg: 'rgba(220,38,38,0.15)',   color: '#b91c1c', label: 'Blocked' },
};
const STATUS_ORDER = ['backlog','selected','in_progress','done','wont_do','blocked'];

const TYPE_STYLES = {
  pain_point:  { bg: 'rgba(244,63,94,0.10)', color: '#be123c', label: 'Pain Point' },
  opportunity: { bg: 'rgba(16,185,129,0.10)', color: '#047857', label: 'Opportunity' },
  initiative:  { bg: 'rgba(99,102,241,0.10)', color: '#4338ca', label: 'Initiative' },
  risk:        { bg: 'rgba(245,158,11,0.12)', color: '#92400e', label: 'Risk' },
  quick_win:   { bg: 'rgba(34,197,94,0.12)',  color: '#15803d', label: 'Quick Win' },
};
const TYPE_OPTIONS = ['', ...Object.keys(TYPE_STYLES)];

function StatusChip({ status, onChange, readOnly = false }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.backlog;
  if (readOnly || !onChange) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: s.bg, color: s.color }}>
        {s.label}
      </span>
    );
  }
  return (
    <select
      value={status}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs font-semibold rounded-full border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 px-2 py-0.5"
      style={{ background: s.bg, color: s.color }}
      onClick={(e) => e.stopPropagation()}
    >
      {STATUS_ORDER.map((k) => (
        <option key={k} value={k} style={{ background: 'white', color: '#111' }}>
          {STATUS_STYLES[k].label}
        </option>
      ))}
    </select>
  );
}

function TypeChip({ type }) {
  const s = TYPE_STYLES[type] || TYPE_STYLES.pain_point;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium"
          style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function fmtMoney(n) {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (Math.abs(num) >= 1_000)     return `$${Math.round(num / 1000)}K`;
  return `$${num.toLocaleString()}`;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
}

// ── Detail drawer ───────────────────────────────────────────────────────────
function ItemDrawer({ itemId, onClose, onItemChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/items/${encId(itemId)}`, { headers: authHeaders() });
      if (!r.ok) throw new Error(await readErr(r));
      setData(await r.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  async function updateStatus(newStatus) {
    try {
      const r = await fetch(`${API_BASE}/items/${encId(itemId)}`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      if (!r.ok) throw new Error(await readErr(r));
      await load();
      onItemChanged?.();
    } catch (e) { setError(e.message); }
  }

  async function postComment() {
    const body = commentText.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API_BASE}/items/${encId(itemId)}/comments`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ body }),
      });
      if (!r.ok) throw new Error(await readErr(r));
      setCommentText('');
      await load();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }

  const item = data?.item;
  const milestones = data?.milestones || [];
  const comments = data?.comments || [];

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-label="Roadmap item detail">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 shadow-2xl overflow-y-auto flex flex-col">
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-[var(--border)] px-5 py-3 flex items-start justify-between z-10">
          <div className="flex-1 min-w-0 pr-3">
            {item ? (
              <>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {item.source_external_id && (
                    <span className="text-xs font-mono text-gray-400">{item.source_external_id}</span>
                  )}
                  <TypeChip type={item.item_type} />
                  <StatusChip status={item.status} onChange={updateStatus} />
                </div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {item.title || '(untitled)'}
                </h2>
              </>
            ) : (
              <h2 className="text-base font-semibold text-gray-400">Loading…</h2>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-800"
                  aria-label="Close drawer">
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="mx-5 mt-3 p-2 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="px-5 py-8 text-sm text-gray-400">Loading…</div>
        ) : item ? (
          <>
            {/* Core fields */}
            <div className="px-5 py-4 border-b border-[var(--border)]">
              {item.pain_point && (
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-3">
                  {item.pain_point}
                </p>
              )}
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                {item.department && (
                  <><dt className="text-gray-400">Department</dt><dd className="text-gray-800 dark:text-gray-200">{item.department}</dd></>
                )}
                {item.champion && (
                  <><dt className="text-gray-400">Champion</dt><dd className="text-gray-800 dark:text-gray-200">{item.champion}</dd></>
                )}
                {item.estimated_dollars_per_year != null && (
                  <><dt className="text-gray-400">$/year</dt><dd className="text-gray-800 dark:text-gray-200 font-semibold">{fmtMoney(item.estimated_dollars_per_year)}</dd></>
                )}
                {item.combined_score != null && (
                  <><dt className="text-gray-400">Score</dt><dd className="text-gray-800 dark:text-gray-200">{item.combined_score}</dd></>
                )}
                {item.time_lost_weekly && (
                  <><dt className="text-gray-400">Time lost / week</dt><dd className="text-gray-800 dark:text-gray-200">{item.time_lost_weekly}</dd></>
                )}
                {item.target_completion_date && (
                  <><dt className="text-gray-400">Target completion</dt><dd className="text-gray-800 dark:text-gray-200">{fmtDate(item.target_completion_date)}</dd></>
                )}
                {item.is_quick_win && (
                  <><dt className="text-gray-400">Quick win?</dt><dd className="text-emerald-700 font-semibold">yes</dd></>
                )}
              </dl>
              {item.value_statement && (
                <div className="mt-3 text-xs">
                  <div className="text-gray-400 mb-1 font-semibold uppercase tracking-wide">Value</div>
                  <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{item.value_statement}</div>
                </div>
              )}
              {item.current_workaround && (
                <div className="mt-3 text-xs">
                  <div className="text-gray-400 mb-1 font-semibold uppercase tracking-wide">Current workaround</div>
                  <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{item.current_workaround}</div>
                </div>
              )}
            </div>

            {/* Milestones */}
            {milestones.length > 0 && (
              <div className="px-5 py-4 border-b border-[var(--border)]">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Milestones ({milestones.length})
                </div>
                <ul className="space-y-1.5">
                  {milestones.map((m) => (
                    <li key={m.id} className="text-sm flex items-start gap-2">
                      <span className={`mt-1 inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                        m.status === 'completed' ? 'bg-green-500' :
                        m.status === 'in_progress' ? 'bg-amber-500' :
                        m.status === 'blocked' ? 'bg-red-500' : 'bg-gray-300'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 dark:text-gray-200">{m.title}</div>
                        {m.description && <div className="text-xs text-gray-500 mt-0.5">{m.description}</div>}
                        <div className="text-[11px] text-gray-400 mt-0.5">
                          {m.owner && <>by {m.owner} · </>}
                          {m.due_date ? `due ${fmtDate(m.due_date)}` : 'no due date'}
                          {m.progress > 0 && ` · ${m.progress}%`}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Comment thread */}
            <div className="px-5 py-4 flex-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Discussion ({comments.length})
              </div>
              {comments.length === 0 ? (
                <div className="text-xs text-gray-400 italic mb-3">No comments yet — start the discussion below.</div>
              ) : (
                <ul className="space-y-2 mb-3">
                  {comments.map((c) => (
                    <li key={c.id} className="text-sm bg-gray-50 dark:bg-slate-800 rounded-lg p-2.5">
                      <div className="text-[11px] text-gray-500 mb-0.5">
                        <span className="font-medium">{c.author_name || c.author_email || 'unknown'}</span>
                        {' · '}
                        <span>{fmtDate(c.created_at)}</span>
                      </div>
                      <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{c.body}</div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex gap-2 items-start">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment…"
                  rows={2}
                  className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100"
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postComment(); }}
                />
                <button
                  onClick={postComment}
                  disabled={!commentText.trim() || submitting}
                  className="px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                >
                  <MessageSquare size={14} />
                  Post
                </button>
              </div>
              <div className="text-[10px] text-gray-400 mt-1">⌘/Ctrl-Enter to post</div>
            </div>
          </>
        ) : (
          <div className="px-5 py-8 text-sm text-gray-400">Item not found.</div>
        )}
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
const TABS = [
  { key: 'items',       label: 'Items',         icon: ChevronRight },
  { key: 'roi',         label: 'ROI Dashboard', icon: TrendingUp },
  { key: 'commitments', label: 'Commitments',   icon: CheckSquare },
  { key: 'meetings',    label: 'Meetings',      icon: CalendarDays },
];

function RoiTab() {
  const [data, setData] = useState(null);
  const [windowDays, setWindowDays] = useState(365);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [itemTitles, setItemTitles] = useState({});

  useEffect(() => {
    // Tracks whether this effect-instance is still the latest. If the user
    // changes windowDays mid-flight or unmounts, we drop the stale result
    // instead of overwriting fresh state. Also normalize nested fields so a
    // missing key in the server payload can't crash the dashboard.
    let alive = true;
    setLoading(true); setError(null);
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/roi-summary?window_days=${windowDays}`, { headers: authHeaders() });
        if (!r.ok) throw new Error(await readErr(r));
        const body = await r.json();
        if (!alive) return;
        const normalized = {
          totals:    { dollars: Number(body?.totals?.dollars)    || 0, hours: Number(body?.totals?.hours)    || 0 },
          realized:  { dollars: Number(body?.realized?.dollars)  || 0, hours: Number(body?.realized?.hours)  || 0 },
          projected: { dollars: Number(body?.projected?.dollars) || 0, hours: Number(body?.projected?.hours) || 0 },
          last_30d:  { dollars: Number(body?.last_30d?.dollars)  || 0, hours: Number(body?.last_30d?.hours)  || 0 },
          event_count: Number(body?.event_count) || 0,
          // Server caps at 10; client-side cap is defense in depth in case
          // the API contract changes — bounds the N+1 title-fetch fan-out.
          top_items: (Array.isArray(body?.top_items) ? body.top_items : []).slice(0, 10),
        };
        setData(normalized);
        const ids = normalized.top_items.map(t => t.item_id).filter(Boolean);
        if (ids.length) {
          const titles = {};
          await Promise.all(ids.map(async id => {
            try {
              const ir = await fetch(`${API_BASE}/items/${encId(id)}`, { headers: authHeaders() });
              if (ir.ok) {
                const ib = await ir.json();
                titles[id] = ib.item?.title || ib.item?.source_external_id || id;
              }
            } catch {/* ignore one-off */}
          }));
          if (alive) setItemTitles(titles);
        } else if (alive) {
          setItemTitles({});
        }
      } catch (e) { if (alive) setError(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [windowDays]);

  const empty = !loading && data && data.event_count === 0;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">ROI Dashboard</h2>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">Aggregate of recorded ROI events. Capture events on individual items to populate.</p>
        </div>
        <select value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))}
                className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100">
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 365 days</option>
          <option value={1825}>Last 5 years</option>
        </select>
      </div>
      {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
      {loading && !data && <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>}
      {data && (
        <>
          {empty && (
            <div className="bg-blue-50 dark:bg-slate-900 border border-blue-200 dark:border-slate-700 rounded-xl p-6 text-sm text-blue-900 dark:text-gray-300">
              <div className="font-semibold mb-1">No ROI events recorded yet.</div>
              <div>Capture ROI events on an item's drawer to populate this dashboard. Each event records realized or projected dollars/hours saved.</div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <RoiStatCard label="Total $ (window)"      value={fmtMoney(data.totals.dollars)}  sub={`${data.event_count} events`} accent="#15803d" />
            <RoiStatCard label="Realized $"            value={fmtMoney(data.realized.dollars)} sub={`${data.realized.hours.toLocaleString()} hrs`} accent="#1d4ed8" />
            <RoiStatCard label="Projected $"           value={fmtMoney(data.projected.dollars)} sub={`${data.projected.hours.toLocaleString()} hrs`} accent="#92400e" />
            <RoiStatCard label="Last 30 days $"        value={fmtMoney(data.last_30d.dollars)} sub={`${data.last_30d.hours.toLocaleString()} hrs`} accent="#4338ca" />
          </div>
          {data.top_items && data.top_items.length > 0 && (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-[var(--border)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--border)] text-sm font-semibold text-gray-700 dark:text-gray-200">
                Top items by realized impact (window)
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-slate-800 border-b border-[var(--border)]">
                  <tr className="text-left text-xs text-gray-500">
                    <th className="px-3 py-2 font-semibold">Item</th>
                    <th className="px-3 py-2 font-semibold w-24 text-right">$ impact</th>
                    <th className="px-3 py-2 font-semibold w-24 text-right">Hours</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                  {data.top_items.map((t) => (
                    <tr key={t.item_id}>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100 truncate max-w-md" title={itemTitles[t.item_id] || t.item_id}>
                        {itemTitles[t.item_id] || <span className="font-mono text-xs text-gray-400">{t.item_id.slice(0, 8)}…</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(t.dollars)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{Number(t.hours || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RoiStatCard({ label, value, sub, accent }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-[var(--border)] p-4">
      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</div>
      <div className="text-2xl font-bold mt-1 tabular-nums" style={{ color: accent }}>{value}</div>
      <div className="text-xs text-gray-400 mt-0.5">{sub}</div>
    </div>
  );
}

const COMMITMENT_STATUSES = ['pending','in_progress','completed','cancelled'];
const COMMITMENT_STYLES = {
  pending:     { bg: '#fef3c7', color: '#92400e' },
  in_progress: { bg: 'rgba(59,130,246,0.15)', color: '#1d4ed8' },
  completed:   { bg: 'rgba(22,163,74,0.15)',  color: '#15803d' },
  cancelled:   { bg: 'rgba(100,116,139,0.18)', color: '#475569' },
};

function CommitmentsTab() {
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchCommitments = useCallback(async (signal) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const r = await fetch(`${API_BASE}/commitments?${params}`, { headers: authHeaders(), signal });
      if (!r.ok) throw new Error(await readErr(r));
      const body = await r.json();
      if (signal?.aborted) return;
      setCommitments(body.commitments || []);
    } catch (e) {
      if (e.name === 'AbortError') return;
      setError(e.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    const ac = new AbortController();
    fetchCommitments(ac.signal);
    return () => ac.abort();
  }, [fetchCommitments]);

  // Refetch only on success — on failure we keep the error message visible
  // instead of having the refetch flip setError(null) and hide it from the user.
  async function updateStatus(id, nextStatus) {
    try {
      const r = await fetch(`${API_BASE}/commitments/${encId(id)}`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!r.ok) throw new Error(await readErr(r));
      fetchCommitments();
    } catch (e) {
      setError(`Failed to update: ${e.message}`);
    }
  }

  return (
    <div className="p-6 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Commitments</h2>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">Action items from meetings and item work. Inline status update.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100">
            <option value="">All statuses</option>
            {COMMITMENT_STATUSES.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
          </select>
        </div>
      </div>
      {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
      {loading && commitments.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>
      ) : commitments.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center">
          {statusFilter ? `No commitments with status "${statusFilter}".` : 'No commitments yet. Workshops imported via BCAI will populate this list.'}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-[var(--border)] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-slate-800 border-b border-[var(--border)]">
              <tr className="text-left text-xs text-gray-500">
                <th className="px-3 py-2 font-semibold">Commitment</th>
                <th className="px-3 py-2 font-semibold w-40">Assigned to</th>
                <th className="px-3 py-2 font-semibold w-28">Due</th>
                <th className="px-3 py-2 font-semibold w-36">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {commitments.map((c) => {
                const s = COMMITMENT_STYLES[c.status] || COMMITMENT_STYLES.pending;
                return (
                  <tr key={c.id}>
                    <td className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{c.commitment_text || '(no text)'}</td>
                    <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 text-xs">{c.assigned_to || '—'}</td>
                    <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 text-xs">{c.due_date ? fmtDate(c.due_date) : '—'}</td>
                    <td className="px-3 py-2.5">
                      <select value={c.status} onChange={(e) => updateStatus(c.id, e.target.value)}
                              className="text-xs font-semibold rounded-full border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 px-2 py-0.5"
                              style={{ background: s.bg, color: s.color }}>
                        {COMMITMENT_STATUSES.map(k => (
                          <option key={k} value={k} style={{ background: 'white', color: '#111' }}>{k.replace('_',' ')}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MeetingsTab() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openMeetingId, setOpenMeetingId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/meetings`, { headers: authHeaders() });
        if (!r.ok) throw new Error(await readErr(r));
        const body = await r.json();
        if (alive) setMeetings(body.meetings || []);
      } catch (e) { if (alive) setError(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="p-6 space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Meetings</h2>
        <p className="text-xs text-[var(--text-secondary)] mt-0.5">Workshops, consultations, follow-ups. Click for transcript + attendees + items discussed.</p>
      </div>
      {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
      {loading && meetings.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>
      ) : meetings.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center">No meetings yet. Workshops imported via BCAI will populate this list.</div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-[var(--border)] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-slate-800 border-b border-[var(--border)]">
              <tr className="text-left text-xs text-gray-500">
                <th className="px-3 py-2 font-semibold">Title</th>
                <th className="px-3 py-2 font-semibold w-28">Type</th>
                <th className="px-3 py-2 font-semibold w-28">When</th>
                <th className="px-3 py-2 font-semibold w-32">Attendees</th>
                <th className="px-3 py-2 font-semibold w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {meetings.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/60 cursor-pointer" onClick={() => setOpenMeetingId(m.id)}>
                  <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100">{m.title}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300">{m.meeting_type}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300">{m.occurred_at ? fmtDate(m.occurred_at) : '—'}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300">
                    <span className="inline-flex items-center gap-1"><Users size={12} className="text-gray-400" />{Array.isArray(m.attendees) ? m.attendees.length : 0}</span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-300"><ChevronRight size={14} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openMeetingId && <MeetingDrawer meetingId={openMeetingId} onClose={() => setOpenMeetingId(null)} />}
    </div>
  );
}

function MeetingDrawer({ meetingId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [transcriptDraft, setTranscriptDraft] = useState('');
  const [savingTranscript, setSavingTranscript] = useState(false);
  const [summarizing, setSummarizing] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/meetings/${encId(meetingId)}`, { headers: authHeaders() });
      if (!r.ok) throw new Error(await readErr(r));
      const body = await r.json();
      setData(body);
      setTranscriptDraft(body.transcript?.raw_text || '');
    } catch (e) { setError(e.message); }
  }, [meetingId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/meetings/${encId(meetingId)}`, { headers: authHeaders() });
        if (!r.ok) throw new Error(await readErr(r));
        const body = await r.json();
        if (alive) {
          setData(body);
          setTranscriptDraft(body.transcript?.raw_text || '');
        }
      } catch (e) { if (alive) setError(e.message); }
    })();
    return () => { alive = false; };
  }, [meetingId]);

  async function saveTranscript() {
    setSavingTranscript(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/meetings/${encId(meetingId)}/transcript`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ raw_text: transcriptDraft, source_format: 'plain-text' }),
      });
      if (!r.ok) throw new Error(await readErr(r));
      setEditingTranscript(false);
      await reload();
    } catch (e) { setError(`Failed to save transcript: ${e.message}`); }
    finally { setSavingTranscript(false); }
  }

  async function generateSummary() {
    setSummarizing(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/meetings/${encId(meetingId)}/summarize`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!r.ok) throw new Error(await readErr(r));
      await reload();
    } catch (e) { setError(`Summarization failed: ${e.message}`); }
    finally { setSummarizing(false); }
  }

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-[640px] max-w-full bg-white dark:bg-slate-950 shadow-2xl overflow-y-auto border-l border-[var(--border)]">
        <div className="sticky top-0 bg-white dark:bg-slate-950 border-b border-[var(--border)] px-6 py-4 flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
              {data?.meeting?.title || 'Loading…'}
            </h3>
            <div className="text-xs text-gray-500 mt-0.5">
              {data?.meeting?.meeting_type || ''}
              {data?.meeting?.occurred_at ? ` · ${fmtDate(data.meeting.occurred_at)}` : ''}
              {data?.meeting?.facilitator ? ` · Facilitator: ${data.meeting.facilitator}` : ''}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-800"><X size={18} /></button>
        </div>
        {error && <div className="mx-6 mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
        {data && (
          <div className="px-6 py-4 space-y-5">
            <section>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-1.5">
                <Users size={14} /> Attendees ({Array.isArray(data.meeting.attendees) ? data.meeting.attendees.length : 0})
              </h4>
              {Array.isArray(data.meeting.attendees) && data.meeting.attendees.length > 0 ? (
                <ul className="space-y-1.5">
                  {data.meeting.attendees.map((a, i) => (
                    <li key={i} className="text-sm text-gray-800 dark:text-gray-200">
                      <span className="font-medium">{a.name || '(unnamed)'}</span>
                      {a.role && <span className="text-gray-500"> · {a.role}</span>}
                      {a.department && <span className="text-gray-400"> · {a.department}</span>}
                      {a.email && <span className="text-gray-400 text-xs"> · {a.email}</span>}
                    </li>
                  ))}
                </ul>
              ) : <div className="text-xs text-gray-400">No attendees recorded.</div>}
            </section>
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                  <FileText size={14} /> Transcript
                  {data.transcript?.raw_text && (
                    <span className="text-xs text-gray-400 font-normal">
                      ({data.transcript.raw_text.length.toLocaleString()} chars)
                    </span>
                  )}
                </h4>
                {!editingTranscript ? (
                  <button onClick={() => setEditingTranscript(true)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                    {data.transcript?.raw_text ? 'Edit' : 'Paste transcript'}
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setEditingTranscript(false); setTranscriptDraft(data.transcript?.raw_text || ''); }}
                            className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                    <button onClick={saveTranscript} disabled={savingTranscript}
                            className="text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1 rounded-md font-medium">
                      {savingTranscript ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
              {editingTranscript ? (
                <textarea
                  value={transcriptDraft}
                  onChange={(e) => setTranscriptDraft(e.target.value)}
                  placeholder="Paste meeting transcript here — plain text, multi-line, max 200K characters."
                  className="w-full h-64 text-xs font-mono p-3 border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              ) : data.transcript?.raw_text ? (
                <div className="bg-gray-50 dark:bg-slate-900 border border-[var(--border)] rounded-lg p-3 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {data.transcript.raw_text}
                </div>
              ) : <div className="text-xs text-gray-400">No transcript captured yet. Click "Paste transcript" to add one.</div>}
            </section>
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">AI Summary</h4>
                {data.transcript?.raw_text && (
                  <button onClick={generateSummary} disabled={summarizing}
                          className="text-xs bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-3 py-1 rounded-md font-medium">
                    {summarizing ? 'Generating…' : data.summary ? 'Regenerate' : 'Generate summary'}
                  </button>
                )}
              </div>
              {data.summary ? (
                <div className="space-y-3">
                  {data.summary.summary_text && (
                    <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{data.summary.summary_text}</div>
                  )}
                  {Array.isArray(data.summary.key_decisions) && data.summary.key_decisions.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">Key decisions</div>
                      <ul className="space-y-1.5">
                        {data.summary.key_decisions.map((d, i) => (
                          <li key={i} className="text-sm text-gray-800 dark:text-gray-200">
                            <span className="font-medium">· {d.decision || ''}</span>
                            {d.rationale && <span className="text-gray-500"> — {d.rationale}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(data.summary.action_items) && data.summary.action_items.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">Action items</div>
                      <ul className="space-y-1.5">
                        {data.summary.action_items.map((a, i) => (
                          <li key={i} className="text-sm text-gray-800 dark:text-gray-200">
                            ☐ {a.text || ''}
                            {a.assigned_to && <span className="text-gray-500"> · {a.assigned_to}</span>}
                            {a.due_date && <span className="text-gray-400 text-xs"> · due {a.due_date}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(data.summary.pain_points_mentioned) && data.summary.pain_points_mentioned.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">Pain points mentioned</div>
                      <div className="flex flex-wrap gap-1">
                        {data.summary.pain_points_mentioned.map((p, i) => (
                          <span key={i} className="text-xs bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 px-2 py-0.5 rounded">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="text-[10px] text-gray-400 uppercase tracking-wide">
                    {data.summary.ai_model || 'ai'} · {data.summary.generated_at ? fmtDate(data.summary.generated_at) : ''}
                  </div>
                </div>
              ) : data.transcript?.raw_text ? (
                <div className="text-xs text-gray-400">No summary generated yet. Click "Generate summary" above.</div>
              ) : (
                <div className="text-xs text-gray-400">Upload a transcript to enable AI summary.</div>
              )}
            </section>
            {data.meeting.notes && (
              <section>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Notes</h4>
                <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{data.meeting.notes}</div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function RoadmapPage() {
  const [tab, setTab] = useState('items');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [status, setStatus] = useState('');
  const [itemType, setItemType] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('-updated_at');

  // Drawer
  const [openItemId, setOpenItemId] = useState(null);

  // Sticky filters in URL? Skip for v1 simplicity.

  const fetchItems = useCallback(async (signal) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (status)   params.set('status', status);
      if (itemType) params.set('item_type', itemType);
      if (q)        params.set('q', q);
      if (sort)     params.set('sort', sort);
      const r = await fetch(`${API_BASE}/items?${params}`, { headers: authHeaders(), signal });
      if (!r.ok) throw new Error(await readErr(r));
      const body = await r.json();
      if (signal?.aborted) return;
      setItems(body.items || []);
    } catch (e) {
      if (e.name === 'AbortError') return;
      setError(e.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [status, itemType, q, sort]);

  // Debounce search-q so we're not firing on every keystroke. AbortController
  // cancels any in-flight stale request when filters change rapidly so the
  // last fetch's result is what wins.
  useEffect(() => {
    const ac = new AbortController();
    const t = setTimeout(() => fetchItems(ac.signal), q ? 250 : 0);
    return () => { clearTimeout(t); ac.abort(); };
  }, [fetchItems, q]);

  // Status summary counts (computed client-side from the current result set;
  // for a tenant with many items past the page-size, swap to a server-side
  // aggregate endpoint).
  const statusCounts = useMemo(() => {
    const c = {};
    for (const it of items) {
      c[it.status] = (c[it.status] || 0) + 1;
    }
    return c;
  }, [items]);

  return (
    <div className="flex-1 flex flex-col h-full bg-gray-50 dark:bg-slate-950">
      {/* Header */}
      <div className="px-6 pt-5 pb-0 flex items-end justify-between flex-wrap gap-3 border-b border-[var(--border)] bg-white dark:bg-slate-900">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Roadmap</h1>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5 mb-3">
            Pain points · opportunities · ROI · commitments. Per-tenant operational backlog.
          </p>
        </div>
        {tab === 'items' && (
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => fetchItems()}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-800"
              title="Refresh"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* Tab nav */}
      <div className="px-6 bg-white dark:bg-slate-900 border-b border-[var(--border)] flex gap-1">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${
                active
                  ? 'border-blue-500 text-blue-700 dark:text-blue-300'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          );
        })}
      </div>

      {tab !== 'items' && (
        <div className="flex-1 overflow-auto">
          {tab === 'roi'         && <RoiTab />}
          {tab === 'commitments' && <CommitmentsTab />}
          {tab === 'meetings'    && <MeetingsTab />}
        </div>
      )}

      {tab === 'items' && (
      <>
      {/* Status summary strip */}
      <div className="px-6 py-3 bg-white dark:bg-slate-900 border-b border-[var(--border)] flex flex-wrap gap-2 items-center">
        <button
          onClick={() => setStatus('')}
          className={`px-2.5 py-1 rounded-full text-xs font-medium transition ${
            !status ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
          }`}
        >
          All ({items.length})
        </button>
        {STATUS_ORDER.map((k) => {
          const n = statusCounts[k] || 0;
          const s = STATUS_STYLES[k];
          const active = status === k;
          return (
            <button
              key={k}
              onClick={() => setStatus(active ? '' : k)}
              className="px-2.5 py-1 rounded-full text-xs font-medium transition border"
              style={{
                background: active ? s.color : s.bg,
                color: active ? 'white' : s.color,
                borderColor: active ? s.color : 'transparent',
              }}
            >
              {s.label} ({n})
            </button>
          );
        })}
      </div>

      {/* Filters bar */}
      <div className="px-6 py-3 bg-white dark:bg-slate-900 border-b border-[var(--border)] flex items-center flex-wrap gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search title / pain / process…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg pl-7 pr-3 py-1.5 w-64 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100"
          />
        </div>
        <select
          value={itemType}
          onChange={(e) => setItemType(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100"
        >
          <option value="">All types</option>
          {TYPE_OPTIONS.filter(Boolean).map((t) => (
            <option key={t} value={t}>{TYPE_STYLES[t].label}</option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100"
        >
          <option value="-updated_at">Recent edit ↓</option>
          <option value="updated_at">Recent edit ↑</option>
          <option value="-estimated_dollars_per_year">$/year ↓</option>
          <option value="-combined_score">Score ↓</option>
          <option value="sort_order">Manual order</option>
          <option value="title">Title A→Z</option>
        </select>
        {(status || itemType || q) && (
          <button
            onClick={() => { setStatus(''); setItemType(''); setQ(''); }}
            className="text-xs text-gray-400 hover:text-gray-600 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div className="mx-6 mt-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {/* Items table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loading && items.length === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center">
            {(status || itemType || q)
              ? 'No items match the current filters.'
              : 'No roadmap items yet. Import from a workshop, or capture one directly.'}
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-[var(--border)] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800 border-b border-[var(--border)]">
                <tr className="text-left text-xs text-gray-500">
                  <th className="px-3 py-2.5 font-semibold w-20">ID</th>
                  <th className="px-3 py-2.5 font-semibold">Item</th>
                  <th className="px-3 py-2.5 font-semibold w-24">Type</th>
                  <th className="px-3 py-2.5 font-semibold w-28">Dept</th>
                  <th className="px-3 py-2.5 font-semibold w-24 text-right">$/year</th>
                  <th className="px-3 py-2.5 font-semibold w-32">Status</th>
                  <th className="px-3 py-2.5 font-semibold w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="hover:bg-gray-50 dark:hover:bg-slate-800/60 cursor-pointer transition-colors"
                    onClick={() => setOpenItemId(it.id)}
                  >
                    <td className="px-3 py-2.5 text-xs font-mono text-gray-400">
                      {it.source_external_id || ''}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-md" title={it.title}>
                        {it.title || '(untitled)'}
                      </div>
                      {it.pain_point && (
                        <div className="text-xs text-gray-500 truncate max-w-md mt-0.5" title={it.pain_point}>
                          {it.pain_point}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5"><TypeChip type={it.item_type} /></td>
                    <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400">{it.department || '—'}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-medium text-gray-800 dark:text-gray-200">
                      {it.estimated_dollars_per_year != null && (
                        <span className="inline-flex items-center gap-0.5">
                          <DollarSign size={11} className="text-gray-400" />
                          {fmtMoney(it.estimated_dollars_per_year).replace('$', '')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <StatusChip status={it.status} onChange={async (s) => {
                        // Only refetch on success — if the PATCH fails, the
                        // error banner needs to stay visible. A finally-block
                        // refetch would call setError(null) and silently
                        // hide the failure from the user.
                        try {
                          const r = await fetch(`${API_BASE}/items/${encId(it.id)}`, {
                            method: 'PATCH', headers: authHeaders(),
                            body: JSON.stringify({ status: s }),
                          });
                          if (!r.ok) throw new Error(await readErr(r));
                          fetchItems();
                        } catch (e) {
                          setError(`Failed to update status: ${e.message}`);
                        }
                      }} />
                    </td>
                    <td className="px-3 py-2.5 text-gray-300">
                      <ChevronRight size={14} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      </>
      )}

      {/* Drawer */}
      {openItemId && (
        <ItemDrawer
          itemId={openItemId}
          onClose={() => setOpenItemId(null)}
          onItemChanged={fetchItems}
        />
      )}
    </div>
  );
}
